/**
 * ArticleEngineReview — Admin editorial review queue for engine-created drafts.
 * Route: /admin/article-engine
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { AdminLayout } from '../components/AdminLayout';
import ImageKitUpload from '../job-board/components/ImageKitUpload';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeader() {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const SECTORS = [
  { value: '', label: 'All Sectors' },
  { value: 'education',       label: 'Education' },
  { value: 'healthcare',      label: 'Healthcare' },
  { value: 'business-finance',label: 'Business & Finance' },
  { value: 'government',      label: 'Government' },
  { value: 'technology-ai',   label: 'Technology & AI' },
  { value: 'workforce-labor', label: 'Workforce & Labor' },
];

const SECTOR_COLOR = {
  'education':       '#5E3F8F',
  'healthcare':      '#1d6f6a',
  'business-finance':'#b45309',
  'government':      '#16304a',
  'technology-ai':   '#1B6B72',
  'workforce-labor': '#b45309',
};

const IMG_STATUS = [
  { value: '',            label: 'All Image States' },
  { value: 'missing',     label: 'Missing Image' },
  { value: 'needs_review',label: 'Needs Review' },
  { value: 'approved',    label: 'Image Approved' },
];

const PUBLISH_RULES = [
  { key: 'draft',        label: 'Status = draft',                        check: d => d.status === 'draft' },
  { key: 'engine',       label: 'Created by engine',                     check: d => d.created_by_engine === true },
  { key: 'pillar',       label: 'Valid primary pillar',                  check: d => ['education','healthcare','business-finance','government','technology-ai','workforce-labor'].includes(d.primary_pillar) },
  { key: 'website',      label: 'distribution_targets includes website', check: d => (d.distribution_targets||[]).includes('website') },
  { key: 'image',        label: 'featured_image_url present',            check: d => !!d.featured_image_url },
  { key: 'rights',       label: 'image_rights_status = approved',        check: d => d.image_rights_status === 'approved' },
  { key: 'title',        label: 'Title present',                         check: d => !!d.title?.trim() },
  { key: 'summary',      label: 'Summary present',                       check: d => !!d.summary?.trim() },
  { key: 'body',         label: 'Body present',                          check: d => !!d.body?.trim() },
];

// ── Source Management ──────────────────────────────────────────────────────────

const TRUST_TIERS = ['authoritative', 'editorial', 'community'];

const BLANK_SOURCE = { name: '', url: '', sector: 'education', enabled: true, trust_tier: 'editorial', default_tags: '' };

function SourceFormModal({ source, onClose, onSaved }) {
  const isEdit = !!source?.id;
  const [form, setForm] = useState(
    isEdit
      ? { ...source, default_tags: (source.default_tags || []).join(', ') }
      : { ...BLANK_SOURCE }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const f = field => e => setForm(p => ({ ...p, [field]: e.target.value }));
  const fBool = field => e => setForm(p => ({ ...p, [field]: e.target.checked }));

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.url.trim()) { setError('Feed URL is required'); return; }
    if (!form.url.startsWith('http')) { setError('Feed URL must start with http'); return; }

    setSaving(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      url: form.url.trim(),
      sector: form.sector,
      enabled: form.enabled,
      trust_tier: form.trust_tier,
      default_tags: form.default_tags ? form.default_tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    };

    try {
      const url = isEdit
        ? `${API}/api/admin/article-engine/sources/${source.id}`
        : `${API}/api/admin/article-engine/sources`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success(isEdit ? 'Source updated' : 'Source added');
      onSaved(data);
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box', background: '#fff' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 5, letterSpacing: '0.04em', textTransform: 'uppercase' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} onClick={onClose} />
      <div style={{ position: 'relative', zIndex: 1, width: 520, maxWidth: '95vw', height: '100vh', overflowY: 'auto', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 3 }}>Article Engine</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{isEdit ? 'Edit Source' : 'Add New Source'}</div>
            </div>
            <button onClick={onClose} style={{ fontSize: 20, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>×</button>
          </div>
        </div>

        {/* Form */}
        <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#991b1b' }}>
              {error}
            </div>
          )}

          <div>
            <label style={labelStyle}>Source Name *</label>
            <input value={form.name} onChange={f('name')} placeholder="e.g. Reuters Health" style={inputStyle} data-testid="source-form-name" />
          </div>

          <div>
            <label style={labelStyle}>RSS Feed URL *</label>
            <input value={form.url} onChange={f('url')} placeholder="https://example.com/rss" style={inputStyle} data-testid="source-form-url" />
          </div>

          <div>
            <label style={labelStyle}>Sector *</label>
            <select value={form.sector} onChange={f('sector')} style={inputStyle} data-testid="source-form-sector">
              {SECTORS.filter(s => s.value).map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Trust Tier</label>
            <select value={form.trust_tier} onChange={f('trust_tier')} style={inputStyle} data-testid="source-form-trust-tier">
              {TRUST_TIERS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Default Tags (comma-separated)</label>
            <input value={form.default_tags} onChange={f('default_tags')} placeholder="e.g. healthcare, policy, workforce" style={inputStyle} data-testid="source-form-tags" />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" checked={form.enabled} onChange={fBool('enabled')} id="source-enabled-toggle" data-testid="source-form-enabled" style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <label htmlFor="source-enabled-toggle" style={{ fontSize: 13, color: '#374151', cursor: 'pointer', fontWeight: 600 }}>
              Active (enabled for ingestion runs)
            </label>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', gap: 8 }}>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{ padding: '9px 20px', background: '#081D30', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}
            data-testid="source-form-submit"
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Source'}
          </button>
          <button onClick={onClose} style={{ padding: '9px 16px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function SourceManagementPanel() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSource, setEditSource] = useState(null);    // null | source object
  const [showAdd, setShowAdd] = useState(false);
  const [filterSector, setFilterSector] = useState('');
  const [toggling, setToggling] = useState({});          // id → bool
  const [deleting, setDeleting] = useState({});          // id → bool

  const fetchSources = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterSector ? `?sector=${filterSector}` : '';
      const res = await fetch(`${API}/api/admin/article-engine/sources${params}`, { headers: authHeader() });
      if (!res.ok) throw new Error('Failed to load sources');
      const data = await res.json();
      setSources(data);
    } catch {
      toast.error('Failed to load sources');
    } finally {
      setLoading(false);
    }
  }, [filterSector]);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const handleToggleEnabled = async (src) => {
    setToggling(p => ({ ...p, [src.id]: true }));
    try {
      const res = await fetch(`${API}/api/admin/article-engine/sources/${src.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ enabled: !src.enabled }),
      });
      if (!res.ok) throw new Error('Toggle failed');
      const updated = await res.json();
      setSources(prev => prev.map(s => s.id === updated.id ? updated : s));
      toast.success(`Source ${updated.enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.message || 'Toggle failed');
    } finally {
      setToggling(p => ({ ...p, [src.id]: false }));
    }
  };

  const handleDelete = async (src) => {
    if (!window.confirm(`Delete "${src.name}"? This will permanently remove the source from the registry.`)) return;
    setDeleting(p => ({ ...p, [src.id]: true }));
    try {
      const res = await fetch(`${API}/api/admin/article-engine/sources/${src.id}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!res.ok) throw new Error('Delete failed');
      setSources(prev => prev.filter(s => s.id !== src.id));
      toast.success(`"${src.name}" removed`);
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeleting(p => ({ ...p, [src.id]: false }));
    }
  };

  const handleSaved = (saved) => {
    setSources(prev => {
      const idx = prev.findIndex(s => s.id === saved.id);
      if (idx >= 0) {
        const next = [...prev]; next[idx] = saved; return next;
      }
      return [...prev, saved];
    });
    setEditSource(null);
    setShowAdd(false);
  };

  // Group by sector
  const grouped = sources.reduce((acc, s) => {
    const sec = s.sector || 'unknown';
    if (!acc[sec]) acc[sec] = [];
    acc[sec].push(s);
    return acc;
  }, {});

  const orderedSectors = ['healthcare','government','business-finance','education','technology-ai','workforce-labor'];

  const totalEnabled  = sources.filter(s => s.enabled).length;
  const totalDisabled = sources.length - totalEnabled;

  return (
    <div data-testid="source-management-panel">
      {/* Sub-header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            {sources.length} sources total — <span style={{ color: '#059669', fontWeight: 600 }}>{totalEnabled} active</span> · <span style={{ color: '#9ca3af' }}>{totalDisabled} disabled</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={filterSector}
            onChange={e => setFilterSector(e.target.value)}
            style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 12px', fontSize: 12, background: '#fff' }}
            data-testid="source-filter-sector"
          >
            {SECTORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button
            onClick={fetchSources}
            style={{ padding: '7px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            data-testid="refresh-sources-btn"
          >
            ↻ Refresh
          </button>
          <button
            onClick={() => setShowAdd(true)}
            style={{ padding: '8px 16px', background: '#1d6f6a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            data-testid="add-source-btn"
          >
            + Add Source
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280', fontSize: 13 }} data-testid="sources-loading">Loading sources…</div>
      ) : sources.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280', fontSize: 13 }} data-testid="sources-empty">No sources found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {orderedSectors.filter(sec => grouped[sec]?.length > 0).map(sec => (
            <div key={sec} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }} data-testid={`source-sector-group-${sec}`}>
              {/* Sector header */}
              <div style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: SECTOR_COLOR[sec] || '#374151' }}>
                  {sec.replace('-', ' & ')}
                </span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  {grouped[sec].filter(s => s.enabled).length}/{grouped[sec].length} active
                </span>
              </div>

              {/* Source rows */}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    <th style={{ padding: '7px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Name</th>
                    <th style={{ padding: '7px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Feed URL</th>
                    <th style={{ padding: '7px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Trust</th>
                    <th style={{ padding: '7px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Health</th>
                    <th style={{ padding: '7px 14px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Last Ins</th>
                    <th style={{ padding: '7px 14px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Enabled</th>
                    <th style={{ padding: '7px 14px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[sec].map((src, i) => (
                    <tr
                      key={src.id}
                      data-testid={`source-row-mgmt-${src.id}`}
                      style={{ borderTop: i === 0 ? 'none' : '1px solid #f3f4f6', background: src.enabled ? '#fff' : '#fafafa', opacity: src.enabled ? 1 : 0.65 }}
                    >
                      <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, color: '#111827', maxWidth: 200 }}>
                        {src.name}
                        {src.default_tags?.length > 0 && (
                          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                            {src.default_tags.slice(0,4).join(', ')}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 11, color: '#6b7280', maxWidth: 260 }}>
                        <a href={src.url} target="_blank" rel="noreferrer" style={{ color: '#1d6f6a', textDecoration: 'none' }} title={src.url}>
                          {src.url.length > 55 ? src.url.slice(0, 55) + '…' : src.url}
                        </a>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: src.trust_tier === 'authoritative' ? '#dbeafe' : '#f3f4f6', color: src.trust_tier === 'authoritative' ? '#1e40af' : '#374151' }}>
                          {src.trust_tier}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <HealthBadge status={src.health_status || 'unknown'} />
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: 12, fontWeight: src.last_run_inserted > 0 ? 700 : 400, color: src.last_run_inserted > 0 ? '#059669' : '#9ca3af' }}>
                        {src.last_run_inserted ?? 0}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleToggleEnabled(src)}
                          disabled={toggling[src.id]}
                          data-testid={`toggle-source-${src.id}`}
                          title={src.enabled ? 'Click to disable' : 'Click to enable'}
                          style={{
                            width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                            background: src.enabled ? '#1d6f6a' : '#d1d5db',
                            position: 'relative', transition: 'background 0.2s',
                            opacity: toggling[src.id] ? 0.6 : 1,
                          }}
                        >
                          <span style={{
                            position: 'absolute', top: 3, left: src.enabled ? 20 : 3,
                            width: 16, height: 16, borderRadius: 8, background: '#fff',
                            transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          }} />
                        </button>
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => setEditSource(src)}
                          data-testid={`edit-source-${src.id}`}
                          style={{ padding: '5px 12px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', marginRight: 6 }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(src)}
                          disabled={deleting[src.id]}
                          data-testid={`delete-source-${src.id}`}
                          style={{ padding: '5px 12px', background: '#fff', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', opacity: deleting[src.id] ? 0.6 : 1 }}
                        >
                          {deleting[src.id] ? '…' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {/* Unknown/other sectors */}
          {Object.keys(grouped).filter(sec => !orderedSectors.includes(sec)).map(sec => (
            <div key={sec} style={{ fontSize: 12, color: '#6b7280', padding: 8 }}>
              {grouped[sec].length} source(s) in unrecognized sector "{sec}"
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit modal */}
      {(showAdd || editSource) && (
        <SourceFormModal
          source={editSource || null}
          onClose={() => { setShowAdd(false); setEditSource(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ── Legacy batch warning ───────────────────────────────────────────────────────

function LegacyBatchWarning({ count }) {
  if (!count) return null;
  return (
    <div style={{ marginTop: 8, fontSize: 11, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, padding: '5px 10px' }}>
      ⚠ {count} legacy queue item{count !== 1 ? 's' : ''} (batch_id=null, status=discovered) will be excluded from all batch operations.
    </div>
  );
}

// ── Source health badge ────────────────────────────────────────────────────────

const HEALTH_BADGE_STYLES = {
  healthy:              { bg: '#d1fae5', color: '#065f46', label: 'healthy' },
  degraded:             { bg: '#fef3c7', color: '#92400e', label: 'degraded' },
  duplicate_saturated:  { bg: '#ffedd5', color: '#9a3412', label: 'dup-sat' },
  failing:              { bg: '#fee2e2', color: '#991b1b', label: 'failing' },
  dead:                 { bg: '#1f2937', color: '#f9fafb', label: 'dead' },
  unknown:              { bg: '#f3f4f6', color: '#6b7280', label: 'unknown' },
};

function HealthBadge({ status }) {
  const s = HEALTH_BADGE_STYLES[status] || HEALTH_BADGE_STYLES.unknown;
  return (
    <span data-testid={`health-badge-${status}`} style={{ background: s.bg, color: s.color, padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, textTransform: 'uppercase' }}>
      {s.label}
    </span>
  );
}

// ── Reusable helpers ───────────────────────────────────────────────────────────

function PillarBadge({ pillar }) {
  const color = SECTOR_COLOR[pillar] || '#666';
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.06em', textTransform: 'uppercase',
      color, background: `${color}18`,
      padding: '2px 8px', borderRadius: 4,
    }}>
      {pillar?.replace('-', ' & ') || '—'}
    </span>
  );
}

function ImageStatusDot({ item }) {
  if (!item.featured_image_url) {
    return <span style={{ color: '#ef4444', fontSize: 11, fontWeight: 600 }}>● Missing</span>;
  }
  if (item.image_rights_status === 'approved') {
    return <span style={{ color: '#16a34a', fontSize: 11, fontWeight: 600 }}>● Approved</span>;
  }
  return <span style={{ color: '#d97706', fontSize: 11, fontWeight: 600 }}>● Needs review</span>;
}

function SafetyChecklist({ item }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {PUBLISH_RULES.map(rule => {
        const pass = rule.check(item);
        return (
          <div key={rule.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            <span style={{ color: pass ? '#16a34a' : '#ef4444', fontSize: 13, flexShrink: 0 }}>
              {pass ? '✓' : '✗'}
            </span>
            <span style={{ color: pass ? '#166534' : '#991b1b' }}>{rule.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Review Modal ───────────────────────────────────────────────────────────────

function ReviewModal({ draft, onClose, onSaved, onPublished, onRejected }) {
  const [form, setForm] = useState({
    title: draft.title || '',
    dek: draft.dek || '',
    summary: draft.summary || '',
    body: draft.body || '',
    tags: (draft.tags || []).join(', '),
    editorial_topics: (draft.editorial_topics || []).join(', '),
    featured_image_url: draft.featured_image_url || '',
    hero_image_url: draft.hero_image_url || '',
    image_caption: draft.image_caption || '',
    image_credit: draft.image_credit || '',
    image_rights_status: draft.image_rights_status || '',
  });
  const [current, setCurrent] = useState(draft);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [tab, setTab] = useState('edit'); // edit | preview | checklist

  const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || '';

  const syncedItem = { ...current, ...form,
    tags: form.tags ? form.tags.split(',').map(s=>s.trim()).filter(Boolean) : [],
    editorial_topics: form.editorial_topics ? form.editorial_topics.split(',').map(s=>s.trim()).filter(Boolean) : [],
  };
  const allRulesPass = PUBLISH_RULES.every(r => r.check(syncedItem));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(s=>s.trim()).filter(Boolean) : [],
        editorial_topics: form.editorial_topics ? form.editorial_topics.split(',').map(s=>s.trim()).filter(Boolean) : [],
      };
      // Remove empty strings so PUT only sets non-empty values
      const cleanPayload = Object.fromEntries(
        Object.entries(payload).filter(([,v]) => v !== '' && v !== null && v !== undefined)
      );
      const res = await fetch(`${API}/api/admin/article-engine/review-drafts/${draft.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(cleanPayload),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Save failed'); }
      const updated = await res.json();
      setCurrent(updated);
      toast.success('Draft saved');
      onSaved(updated);
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handlePublish = async () => {
    if (!allRulesPass) { toast.error('Not all publish requirements are met. Check the Checklist tab.'); return; }
    setPublishing(true);
    try {
      const res = await fetch(`${API}/api/admin/article-engine/review-drafts/${draft.id}/publish`, {
        method: 'POST', headers: authHeader(),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Publish failed'); }
      const result = await res.json();
      toast.success(`Published: "${result.title}"`);
      onPublished(draft.id);
    } catch (err) { toast.error(err.message || 'Publish failed'); }
    finally { setPublishing(false); }
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      const res = await fetch(`${API}/api/admin/article-engine/review-drafts/${draft.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ reason: rejectReason || undefined }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Reject failed'); }
      toast.success('Draft archived/rejected');
      onRejected(draft.id);
    } catch (err) { toast.error(err.message || 'Reject failed'); }
    finally { setRejecting(false); }
  };

  const f = field => e => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: 0 }}
      data-testid="review-modal"
    >
      {/* Backdrop */}
      <div
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        style={{
          position: 'relative', zIndex: 1, width: 680, maxWidth: '95vw',
          height: '100vh', overflowY: 'auto', background: '#fff',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column',
        }}
        data-testid="review-modal-panel"
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 3 }}>
                Engine Draft Review
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.3, maxWidth: 480 }}>
                {current.title || form.title || 'Untitled'}
              </div>
            </div>
            <button onClick={onClose} style={{ fontSize: 20, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', flexShrink: 0 }}>×</button>
          </div>

          {/* Meta row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10, alignItems: 'center' }}>
            <PillarBadge pillar={current.primary_pillar} />
            {current.source_name && (
              <span style={{ fontSize: 11, color: '#6b7280' }}>via {current.source_name}</span>
            )}
            {current.created_at && (
              <span style={{ fontSize: 11, color: '#9ca3af' }}>
                {new Date(current.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
            {current.word_count > 0 && (
              <span style={{ fontSize: 11, color: '#9ca3af' }}>{current.word_count} words</span>
            )}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginTop: 12 }}>
            {['edit', 'image', 'checklist'].map(t => (
              <button key={t} onClick={() => setTab(t)}
                style={{
                  padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: tab === t ? '#081D30' : '#f3f4f6',
                  color: tab === t ? '#fff' : '#374151',
                }}
                data-testid={`modal-tab-${t}`}
              >
                {t === 'edit' ? 'Edit' : t === 'image' ? 'Image' : 'Checklist'}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* ── Edit Tab ── */}
          {tab === 'edit' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Source info (read-only) */}
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 8 }}>Source Metadata</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 11, color: '#374151' }}>
                  <span><strong>Source:</strong> {current.source_name || '—'}</span>
                  <span><strong>Original title:</strong> {current.original_title?.slice(0,50) || '—'}{(current.original_title?.length||0)>50 ? '…' : ''}</span>
                  {current.source_url && <span><strong>Source URL:</strong> <a href={current.source_url} target="_blank" rel="noreferrer" style={{ color: '#1B6B72' }}>View →</a></span>}
                  {current.source_published_at && <span><strong>Published:</strong> {new Date(current.source_published_at).toLocaleDateString()}</span>}
                </div>
              </div>

              {/* Title */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Title *</label>
                <input value={form.title} onChange={f('title')} style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }} data-testid="edit-title" />
              </div>

              {/* Dek */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Dek (subtitle)</label>
                <input value={form.dek} onChange={f('dek')} style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }} data-testid="edit-dek" />
              </div>

              {/* Summary */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Summary *</label>
                <textarea value={form.summary} onChange={f('summary')} rows={3} style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} data-testid="edit-summary" />
              </div>

              {/* Body */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Body (HTML) *</label>
                <textarea value={form.body} onChange={f('body')} rows={14} style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }} data-testid="edit-body" />
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>
                  {form.body ? Math.round(form.body.split(/\s+/).filter(Boolean).length) : 0} words
                </div>
              </div>

              {/* Tags */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Tags (comma-separated)</label>
                <input value={form.tags} onChange={f('tags')} placeholder="e.g. workforce, policy, education" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }} data-testid="edit-tags" />
              </div>

              {/* Editorial Topics */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Editorial Topics (comma-separated)</label>
                <input value={form.editorial_topics} onChange={f('editorial_topics')} placeholder="e.g. career_mobility, workforce_development" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }} data-testid="edit-editorial-topics" />
              </div>
            </div>
          )}

          {/* ── Image Tab ── */}
          {tab === 'image' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

              {/* Current featured image */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Featured Image</div>
                {form.featured_image_url ? (
                  <img src={form.featured_image_url} alt="" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, marginBottom: 8, border: '1px solid #e5e7eb' }} data-testid="featured-image-preview" />
                ) : (
                  <div style={{ height: 120, background: '#f3f4f6', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 12, marginBottom: 8 }}>
                    No image set
                  </div>
                )}
                <label style={{ display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 5 }}>ImageKit URL (paste or upload below)</label>
                <input value={form.featured_image_url} onChange={f('featured_image_url')} placeholder="https://ik.imagekit.io/..." style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 11px', fontSize: 12, boxSizing: 'border-box', marginBottom: 8 }} data-testid="edit-featured-image-url" />
                <ImageKitUpload
                  folder="op-editorial/"
                  accept="image/*"
                  label="Upload Featured Image"
                  token={token}
                  onUploaded={url => setForm(prev => ({ ...prev, featured_image_url: url }))}
                />
              </div>

              {/* Hero image */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Hero Image URL (optional)</div>
                <input value={form.hero_image_url} onChange={f('hero_image_url')} placeholder="https://ik.imagekit.io/..." style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 11px', fontSize: 12, boxSizing: 'border-box', marginBottom: 8 }} data-testid="edit-hero-image-url" />
                <ImageKitUpload
                  folder="op-editorial/"
                  accept="image/*"
                  label="Upload Hero Image"
                  token={token}
                  onUploaded={url => setForm(prev => ({ ...prev, hero_image_url: url }))}
                />
              </div>

              {/* Caption & credit */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Image Caption</label>
                  <input value={form.image_caption} onChange={f('image_caption')} style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 11px', fontSize: 12, boxSizing: 'border-box' }} data-testid="edit-image-caption" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Image Credit</label>
                  <input value={form.image_credit} onChange={f('image_credit')} style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 11px', fontSize: 12, boxSizing: 'border-box' }} data-testid="edit-image-credit" />
                </div>
              </div>

              {/* Image rights */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Image Rights Status</label>
                <select value={form.image_rights_status} onChange={f('image_rights_status')}
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 11px', fontSize: 13, background: '#fff', boxSizing: 'border-box' }}
                  data-testid="edit-image-rights"
                >
                  <option value="">— Select —</option>
                  <option value="pending">Pending review</option>
                  <option value="needs_review">Needs review</option>
                  <option value="approved">Approved ✓</option>
                  <option value="restricted">Restricted — do not use</option>
                </select>
                {form.image_rights_status === 'approved' && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
                    ✓ Image rights approved — this draft can now be published
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Checklist Tab ── */}
          {tab === 'checklist' && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Publish Safety Checklist</div>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', marginBottom: 14 }}>
                <SafetyChecklist item={syncedItem} />
              </div>
              {allRulesPass ? (
                <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, padding: '12px 14px', fontSize: 12, color: '#166534', fontWeight: 600 }}>
                  ✓ All checks pass — this draft is ready to publish.
                </div>
              ) : (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 14px', fontSize: 12, color: '#991b1b' }}>
                  ✗ Some checks are failing. Save changes and re-check, or use the Image tab to add/approve an image.
                </div>
              )}

              {/* Source metadata */}
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 8 }}>Full Source Metadata</div>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  {[
                    ['source_name', current.source_name],
                    ['source_url', current.source_url],
                    ['original_title', current.original_title],
                    ['source_published_at', current.source_published_at],
                    ['image_source_url', current.image_source_url],
                    ['article_engine_queue_id', current.article_engine_queue_id],
                  ].map(([k, v]) => v ? (
                    <tr key={k}>
                      <td style={{ padding: '3px 0', color: '#6b7280', width: 180, verticalAlign: 'top' }}>{k}</td>
                      <td style={{ padding: '3px 0', color: '#111827', wordBreak: 'break-all' }}>
                        {k.includes('url') && v ? <a href={v} target="_blank" rel="noreferrer" style={{ color: '#1B6B72' }}>{v.slice(0,60)}{v.length>60?'…':''}</a> : v}
                      </td>
                    </tr>
                  ) : null)}
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Save */}
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 16px', background: '#081D30', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}
            data-testid="save-draft-btn"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>

          {/* Publish */}
          <button onClick={handlePublish} disabled={publishing || !allRulesPass}
            style={{
              padding: '8px 16px', background: allRulesPass ? '#16a34a' : '#d1d5db',
              color: allRulesPass ? '#fff' : '#6b7280',
              border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: allRulesPass ? 'pointer' : 'not-allowed', opacity: publishing ? 0.7 : 1
            }}
            title={!allRulesPass ? 'Complete all checklist requirements first' : ''}
            data-testid="publish-draft-btn"
          >
            {publishing ? 'Publishing…' : '✓ Publish'}
          </button>

          {/* Reject */}
          {!showRejectConfirm ? (
            <button onClick={() => setShowRejectConfirm(true)}
              style={{ padding: '8px 16px', background: '#fff', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}
              data-testid="reject-draft-btn"
            >
              Reject / Archive
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto', flexWrap: 'wrap' }}>
              <input
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Reason (optional)"
                style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px', fontSize: 12, width: 180 }}
                data-testid="reject-reason-input"
              />
              <button onClick={handleReject} disabled={rejecting}
                style={{ padding: '7px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                data-testid="confirm-reject-btn"
              >
                {rejecting ? '…' : 'Confirm Reject'}
              </button>
              <button onClick={() => setShowRejectConfirm(false)}
                style={{ padding: '7px 10px', background: 'none', border: '1px solid #d1d5db', color: '#6b7280', borderRadius: 7, fontSize: 12, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Draft row ──────────────────────────────────────────────────────────────────

function DraftRow({ item, selected, onToggle, onOpen }) {
  const canPublish = PUBLISH_RULES.every(r => r.check(item));
  return (
    <tr
      data-testid={`draft-row-${item.id}`}
      style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
      onClick={() => onOpen(item)}
    >
      <td style={{ padding: '10px 12px', width: 36 }} onClick={e => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={() => onToggle(item.id)}
          data-testid={`select-draft-${item.id}`} />
      </td>
      <td style={{ padding: '10px 12px', maxWidth: 280 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.3, marginBottom: 3 }}>
          {item.title?.slice(0, 70)}{(item.title?.length || 0) > 70 ? '…' : ''}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280' }}>
          {item.source_name || '—'} · {item.word_count || '?'} words
        </div>
      </td>
      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
        <PillarBadge pillar={item.primary_pillar} />
      </td>
      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
        <ImageStatusDot item={item} />
      </td>
      <td style={{ padding: '10px 12px', fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>
        {item._queue_status || '—'}
      </td>
      <td style={{ padding: '10px 12px', fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>
        {item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
        {canPublish ? (
          <span style={{ fontSize: 10, fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: 20 }}>Ready</span>
        ) : (
          <span style={{ fontSize: 10, fontWeight: 600, color: '#d97706', background: '#fef3c7', padding: '2px 8px', borderRadius: 20 }}>Needs work</span>
        )}
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ArticleEngineReview() {
  const [activeTab, setActiveTab] = useState('drafts'); // drafts | sources
  const [drafts, setDrafts] = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sector: '', image_status: '', search: '' });
  const [selected, setSelected] = useState(new Set());
  const [openDraft, setOpenDraft] = useState(null);
  const [bulkPublishing, setBulkPublishing] = useState(false);

  // ── Today's batch state ───────────────────────────────────────────────────
  const [batchSummary, setBatchSummary]   = useState(null);   // daily-batch API response
  const [batchRunning, setBatchRunning]   = useState(false);
  const [batchRewriting, setBatchRewriting] = useState(false);
  const [batchRewriteProgress, setBatchRewriteProgress] = useState(null); // {processed, total, pct}
  const [batchImaging, setBatchImaging]   = useState(false);
  const [batchRefreshing, setBatchRefreshing] = useState(false);
  const [batchResetting, setBatchResetting] = useState(false);
  const [batchHistory, setBatchHistory]   = useState([]);
  const [legacyQueueCount, setLegacyQueueCount] = useState(0);

  // ── Source health state ───────────────────────────────────────────────────
  const [sourceHealth, setSourceHealth]         = useState(null);
  const [sourceHealthOpen, setSourceHealthOpen] = useState(false);
  const [sourceHealthLoading, setSourceHealthLoading] = useState(false);

  const todayDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  const activeBatchId  = batchSummary?.batch_id  || null;
  const activeBatchDate = batchSummary?.run_date || todayDate;

  const fetchBatchSummary = useCallback(async (date) => {
    setBatchRefreshing(true);
    try {
      const d = date || todayDate;
      const res = await fetch(`${API}/api/admin/article-engine/daily-batch?date=${d}`, { headers: authHeader() });
      if (!res.ok) throw new Error('Failed to fetch batch summary');
      const data = await res.json();
      setBatchSummary(data);
    } catch {
      // non-fatal
    } finally {
      setBatchRefreshing(false);
    }
  }, [todayDate]);

  const fetchBatchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/article-engine/batch-history?limit=3`, { headers: authHeader() });
      if (!res.ok) return;
      const data = await res.json();
      setBatchHistory(data);
    } catch {
      // non-fatal
    }
  }, []);

  const fetchLegacyCount = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/article-engine/queue?limit=200`, { headers: authHeader() });
      if (!res.ok) return;
      const data = await res.json();
      const count = (data.items || []).filter(i => !i.batch_id && i.status === 'discovered').length;
      setLegacyQueueCount(count);
    } catch {
      // non-fatal
    }
  }, []);

  const fetchSourceHealth = useCallback(async () => {
    setSourceHealthLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/article-engine/sources/health`, { headers: authHeader() });
      if (!res.ok) return;
      const data = await res.json();
      setSourceHealth(data);
    } catch {
      // non-fatal
    } finally {
      setSourceHealthLoading(false);
    }
  }, []);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filters.sector)       params.set('sector', filters.sector);
      if (filters.image_status) params.set('image_status', filters.image_status);
      if (filters.search)       params.set('search', filters.search);
      if (activeBatchId)        params.set('batch_id', activeBatchId);  // batch_id-authoritative
      const res = await fetch(`${API}/api/admin/article-engine/review-drafts?${params}`, { headers: authHeader() });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setDrafts(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load drafts');
    } finally {
      setLoading(false);
    }
  }, [filters, activeBatchId]);

  // Effect 1: Load batch summary + legacy count on mount (stable deps, runs once)
  useEffect(() => {
    fetchBatchSummary();
    fetchLegacyCount();
    fetchSourceHealth();
    fetchBatchHistory();
  }, [fetchBatchSummary, fetchLegacyCount, fetchSourceHealth, fetchBatchHistory]);

  // Effect 2: Reload drafts whenever filters or activeBatchId changes
  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  // ── Batch workflow handlers ───────────────────────────────────────────────

  const handleResetBatch = async () => {
    setBatchResetting(true);
    try {
      const res = await fetch(`${API}/api/admin/article-engine/reset-batch`, {
        method: 'POST',
        headers: authHeader(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      const qDel = data.queue_items_deleted ?? 0;
      const dDel = data.draft_content_items_deleted ?? 0;
      toast.success(`Full batch reset: ${qDel} queue item${qDel !== 1 ? 's' : ''} and ${dDel} draft${dDel !== 1 ? 's' : ''} cleared. Ready for a fresh run.`);
      await fetchBatchSummary();
      await fetchBatchHistory();
    } catch (e) {
      toast.error(`Reset failed: ${e.message}`);
    } finally {
      setBatchResetting(false);
    }
  };

  const handleRunBatch = async () => {
    setBatchRunning(true);
    try {
      const res = await fetch(`${API}/api/admin/article-engine/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Run failed');
      const ins = data.items_inserted ?? 0;
      toast.success(`Run complete — ${ins} new item${ins !== 1 ? 's' : ''} queued (batch: ${(data.batch_id||'').slice(0,8)}…)`);
      await fetchBatchSummary(data.run_date);
    } catch (err) {
      toast.error(err.message || 'Run failed');
    } finally {
      setBatchRunning(false);
    }
  };

  const handleRewriteBatch = async () => {
    if (!activeBatchId && !activeBatchDate) {
      toast.error('No active batch — run Today\'s Batch first.');
      return;
    }
    setBatchRewriting(true);
    try {
      const body = activeBatchId ? { batch_id: activeBatchId } : { run_date: activeBatchDate };
      // Step 1: kick off background job — returns immediately with job_id
      const startRes = await fetch(`${API}/api/admin/article-engine/rewrite-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body),
      });
      const startData = await startRes.json();
      if (!startRes.ok) throw new Error(startData.detail || 'Failed to start rewrite');

      // If the response is the old sync format (no job_id), handle it directly
      if (!startData.job_id) {
        const msg = `Rewritten: ${startData.rewritten} · Failed: ${startData.failed} · Already done: ${startData.already_rewritten} · Legacy excluded: ${startData.ignored_legacy_no_batch_count}`;
        startData.rewritten > 0 ? toast.success(msg) : toast.error(msg);
        await fetchBatchSummary(activeBatchDate);
        fetchDrafts();
        setBatchRewriting(false);
        return;
      }

      const jobId = startData.job_id;
      const totalItems = startData.total_items || 0;
      if (totalItems === 0) {
        toast.error('No items to rewrite in this batch.');
        setBatchRewriting(false);
        return;
      }

      // Step 2: poll /rewrite-batch/status/{job_id} every 2s
      const poll = async () => {
        try {
          const statusRes = await fetch(`${API}/api/admin/article-engine/rewrite-batch/status/${jobId}`, {
            headers: authHeader(),
          });
          if (!statusRes.ok) {
            toast.error('Lost track of rewrite job — refresh to see results.');
            setBatchRewriting(false);
            return;
          }
          const job = await statusRes.json();

          if (job.status === 'complete' || job.status === 'failed') {
            const msg = `Rewritten: ${job.rewritten} · Failed: ${job.failed} · Already done: ${job.already_rewritten} · Legacy excluded: ${job.ignored_legacy_no_batch_count}`;

            // Surface the actual error reason from first failed item
            let errorDetail = '';
            if (job.failed > 0) {
              const firstFail = (job.results || []).find(r => r.status === 'failed' && r.error);
              if (firstFail?.error) {
                const raw = firstFail.error;
                if (raw.includes('spend limit') || raw.includes('budget') || raw.includes('quota')) {
                  errorDetail = ' — Daily LLM spend limit reached. Go to Profile → Manage Plan → Universal Key → Add Balance.';
                } else if (raw.includes('rate limit') || raw.includes('429')) {
                  errorDetail = ' — Rate limit hit. Wait a moment and retry.';
                } else {
                  errorDetail = ` — ${raw.slice(0, 120)}`;
                }
              }
            }

            if (job.rewritten > 0) {
              toast.success(msg);
            } else {
              toast.error(msg + errorDetail, { duration: 10000 });
            }
            await fetchBatchSummary(activeBatchDate);
            fetchDrafts();
            setBatchRewriting(false);
          } else {
            // Still running — show progress and poll again
            const pct = totalItems > 0 ? Math.round((job.processed / totalItems) * 100) : 0;
            // Update button label via state (handled below)
            setBatchRewriteProgress({ processed: job.processed, total: totalItems, pct });
            setTimeout(poll, 2000);
          }
        } catch {
          toast.error('Rewrite polling error — refresh to check results.');
          setBatchRewriting(false);
        }
      };

      setTimeout(poll, 1500);
    } catch (err) {
      toast.error(err.message || 'Rewrite failed');
      setBatchRewriting(false);
    }
  };

  const handleGenerateImagesBatch = async () => {
    if (!activeBatchId && !activeBatchDate) {
      toast.error('No active batch — run Today\'s Batch first.');
      return;
    }
    setBatchImaging(true);
    try {
      const body = activeBatchId ? { batch_id: activeBatchId } : { run_date: activeBatchDate };
      const res = await fetch(`${API}/api/admin/article-engine/generate-draft-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Image generation failed');

      // total_items = 0 means no drafts found
      if ((data.total_items ?? 0) === 0) {
        toast.info('No unimaged drafts found for this batch.');
        setBatchImaging(false);
        return;
      }

      const jobId = data.job_id;
      if (!jobId) throw new Error('No job ID returned from image gen endpoint');

      // ── Poll for job completion ────────────────────────────────────────────
      const poll = async () => {
        try {
          const statusRes = await fetch(
            `${API}/api/admin/article-engine/generate-images/status/${jobId}`,
            { headers: authHeader() }
          );
          if (!statusRes.ok) {
            toast.error('Lost track of image job — refresh to see results.');
            setBatchImaging(false);
            return;
          }
          const job = await statusRes.json();
          if (job.status === 'running') {
            setBatchImaging(true);
            setTimeout(poll, 2000);
            return;
          }
          if (job.status === 'complete' || job.status === 'failed') {
            const msg = `Images: ${job.succeeded ?? 0} generated · ${job.failed ?? 0} failed (of ${job.processed ?? 0})`;
            // Surface first error if everything failed
            let errorDetail = '';
            if ((job.failed ?? 0) > 0) {
              const firstFail = (job.results || []).find(r => r.status === 'failed' && r.error);
              if (firstFail?.error) {
                const raw = firstFail.error;
                if (raw.includes('content_policy') || raw.includes('safety') || raw.includes('policy')) {
                  errorDetail = ' — Content policy violation. Try adjusting the prompt.';
                } else if (raw.includes('spend limit') || raw.includes('budget') || raw.includes('quota')) {
                  errorDetail = ' — Daily spend limit reached. Profile → Manage Plan → Add Balance.';
                } else if (raw.includes('401') || raw.includes('invalid_api_key')) {
                  errorDetail = ' — Invalid API key.';
                } else {
                  errorDetail = ` — ${raw.slice(0, 140)}`;
                }
              }
            }
            (job.succeeded ?? 0) > 0 ? toast.success(msg) : toast.error(msg + errorDetail, { duration: 10000 });
            await fetchBatchSummary(activeBatchDate);
            fetchDrafts();
            setBatchImaging(false);
          }
        } catch {
          toast.error('Image polling error — refresh to check results.');
          setBatchImaging(false);
        }
      };
      setTimeout(poll, 2000);
    } catch (err) {
      toast.error(err.message || 'Image generation failed');
      setBatchImaging(false);
    }
  };

  const handleRefreshBatch = () => fetchBatchSummary(activeBatchDate);

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selected.size === drafts.length) setSelected(new Set());
    else setSelected(new Set(drafts.map(d => d.id)));
  };

  const handleBulkPublish = async () => {
    const ids = [...selected];
    if (!ids.length) { toast.error('Select drafts first'); return; }
    if (ids.length > 10) { toast.error('Maximum 10 drafts per bulk publish'); return; }
    if (!window.confirm(`Publish ${ids.length} selected draft(s)? Each will be validated first.`)) return;

    setBulkPublishing(true);
    try {
      const res = await fetch(`${API}/api/admin/article-engine/review-drafts/bulk-publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ item_ids: ids }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Bulk publish failed');

      const pCount = data.published || 0;
      const fCount = data.failed_validation || 0;
      if (pCount > 0) toast.success(`Published ${pCount} draft(s)`);
      if (fCount > 0) toast.error(`${fCount} draft(s) failed validation — see details`);
      if (data.results) {
        data.results.filter(r => r.status === 'failed_validation').forEach(r =>
          toast.error(`"${r.title?.slice(0,40)}…": ${r.reason}`)
        );
      }
      setSelected(new Set());
      fetchDrafts();
    } catch (err) {
      toast.error(err.message || 'Bulk publish failed');
    } finally {
      setBulkPublishing(false);
    }
  };

  const [generatingImages, setGeneratingImages] = useState(false);

  const handleGenerateImages = async () => {
    setGeneratingImages(true);
    try {
      const body = selected.size > 0 ? { item_ids: [...selected] } : {};
      const res = await fetch(`${API}/api/admin/article-engine/generate-draft-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Image generation failed');
      const msg = `Generated: ${data.succeeded ?? 0} · Failed: ${data.failed ?? 0} (of ${data.processed ?? 0} processed)`;
      data.succeeded > 0 ? toast.success(msg) : toast.error(msg);
      fetchDrafts();
    } catch (err) {
      toast.error(err.message || 'Image generation failed');
    } finally {
      setGeneratingImages(false);
    }
  };

  const readyCount = drafts.filter(d => PUBLISH_RULES.every(r => r.check(d))).length;

  return (
    <AdminLayout title="Article Engine Review" data-testid="article-engine-review-page">
      <div style={{ padding: '20px 24px', maxWidth: 1100 }}>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: 0 }} data-testid="aer-page-title">
              Rewrite Articles
            </h1>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
              {activeTab === 'drafts'
                ? (loading ? 'Loading…' : `${total} engine draft${total !== 1 ? 's' : ''} · ${readyCount} ready to publish`)
                : 'Manage RSS feed sources'}
            </p>
          </div>

          {activeTab === 'drafts' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {selected.size > 0 && (
                <button
                  onClick={handleBulkPublish}
                  disabled={bulkPublishing}
                  style={{ padding: '8px 16px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: bulkPublishing ? 0.7 : 1 }}
                  data-testid="bulk-publish-btn"
                >
                  {bulkPublishing ? 'Publishing…' : `Publish ${selected.size} Selected`}
                </button>
              )}
              <button
                onClick={handleGenerateImages}
                disabled={generatingImages}
                style={{ padding: '8px 14px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: generatingImages ? 'not-allowed' : 'pointer', opacity: generatingImages ? 0.7 : 1 }}
                data-testid="generate-images-btn"
                title={selected.size > 0 ? `Generate images for ${selected.size} selected draft(s)` : 'Generate images for all unimaged drafts'}
              >
                {generatingImages ? 'Generating…' : selected.size > 0 ? `Generate Images (${selected.size})` : 'Generate Images'}
              </button>
              <button
                onClick={fetchDrafts}
                style={{ padding: '8px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                data-testid="refresh-drafts-btn"
              >
                ↻ Refresh
              </button>
            </div>
          )}
        </div>

        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e5e7eb', paddingBottom: 0 }}>
          {[
            { key: 'drafts',  label: 'Draft Review' },
            { key: 'sources', label: 'Source Management' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              style={{
                padding: '9px 18px', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
                background: 'none',
                color: activeTab === tab.key ? '#081D30' : '#6b7280',
                borderBottom: activeTab === tab.key ? '2px solid #081D30' : '2px solid transparent',
                marginBottom: -1,
                borderRadius: 0,
                transition: 'color 0.15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Source Management Tab */}
        {activeTab === 'sources' && <SourceManagementPanel />}

        {/* Drafts Review Tab */}
        {activeTab === 'drafts' && (<>

        {/* ── Today's Batch production control row ─────────────────────────── */}
        <div
          data-testid="batch-control-row"
          style={{ background: '#f8f9fb', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {/* Left: date + Reset Batch */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#16304a', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Today's Batch — {activeBatchDate}
              </span>
              <button
                onClick={handleResetBatch}
                disabled={batchResetting}
                data-testid="reset-batch-btn"
                style={{ padding: '3px 10px', background: 'transparent', color: '#16a34a', border: '1px solid #16a34a', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: batchResetting ? 'not-allowed' : 'pointer', opacity: batchResetting ? 0.6 : 1, letterSpacing: '0.02em' }}
              >
                {batchResetting ? 'Resetting…' : 'Reset Batch'}
              </button>
            </div>

            {/* Right: action buttons + last 3 timestamps */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  onClick={handleRunBatch}
                  disabled={batchRunning}
                  data-testid="run-batch-btn"
                  style={{ padding: '6px 12px', background: '#16304a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: batchRunning ? 'not-allowed' : 'pointer', opacity: batchRunning ? 0.7 : 1 }}
                >
                  {batchRunning ? 'Running…' : 'Run Today\'s Batch'}
                </button>
                <button
                  onClick={handleRewriteBatch}
                  disabled={batchRewriting}
                  data-testid="rewrite-batch-btn"
                  style={{ padding: '6px 12px', background: '#1d6f6a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: batchRewriting ? 'not-allowed' : 'pointer', opacity: batchRewriting ? 0.7 : 1 }}
                >
                  {batchRewriting
                    ? (batchRewriteProgress
                        ? `Rewriting… ${batchRewriteProgress.processed}/${batchRewriteProgress.total}`
                        : 'Starting…')
                    : 'Rewrite Today\'s Batch'}
                </button>
                <button
                  onClick={handleGenerateImagesBatch}
                  disabled={batchImaging}
                  data-testid="generate-images-batch-btn"
                  style={{ padding: '6px 12px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: batchImaging ? 'not-allowed' : 'pointer', opacity: batchImaging ? 0.7 : 1 }}
                >
                  {batchImaging ? 'Generating…' : 'Generate Images for Today\'s Batch'}
                </button>
                <button
                  onClick={handleRefreshBatch}
                  disabled={batchRefreshing}
                  data-testid="refresh-batch-btn"
                  style={{ padding: '6px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: batchRefreshing ? 'not-allowed' : 'pointer', opacity: batchRefreshing ? 0.7 : 1 }}
                >
                  {batchRefreshing ? '…' : '↻ Refresh'}
                </button>
              </div>

              {/* Last 3 batch run timestamps */}
              {batchHistory.length > 0 && (
                <div data-testid="batch-history" style={{ textAlign: 'right', fontSize: 10, color: '#16a34a', fontWeight: 600, lineHeight: 1.7 }}>
                  {batchHistory.map((h, i) => {
                    const ts = h.started_at ? new Date(h.started_at) : null;
                    const label = ts
                      ? ts.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })
                      : h.run_date || '—';
                    return <div key={i}>{label}</div>;
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Thin-day warning banner */}
          {batchSummary?.thin_day_status === 'red' && (
            <div data-testid="thin-day-red" style={{ margin: '8px 0', padding: '8px 12px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12, color: '#991b1b', fontWeight: 600 }}>
              Critical thin batch: Active batch has fewer than 4 articles ({batchSummary.actual_count ?? 0}). Manual editorial review required.
            </div>
          )}
          {batchSummary?.thin_day_status === 'amber' && (
            <div data-testid="thin-day-amber" style={{ margin: '8px 0', padding: '8px 12px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, color: '#92400e', fontWeight: 600 }}>
              Thin batch warning: Active batch has {batchSummary.actual_count ?? 0} of 12 target articles. Review source health before publishing.
            </div>
          )}

          {/* Today's Batch status panel */}
          {batchSummary ? (
            <div data-testid="batch-status-panel" style={{ fontSize: 11, color: '#374151' }}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 6 }}>
                <span><b>Batch ID:</b> {(batchSummary.batch_id || '—').slice(0,12)}{batchSummary.batch_id ? '…' : ''}</span>
                <span><b>Queue items:</b> {batchSummary.total ?? 0}</span>
                <span><b>Rewritten:</b> {batchSummary.summary?.rewritten ?? 0}</span>
                <span><b>Img pending:</b> {batchSummary.summary?.img_pending ?? 0}</span>
                <span><b>Img generated:</b> {batchSummary.summary?.img_generated ?? 0}</span>
                <span><b>Img failed:</b> {batchSummary.summary?.img_failed ?? 0}</span>
                <span><b>Morning:</b> {batchSummary.summary?.morning_wave ?? 0}</span>
                <span><b>Evening:</b> {batchSummary.summary?.evening_wave ?? 0}</span>
              </div>
              {batchSummary.items && batchSummary.items.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  {Object.entries(
                    batchSummary.items.reduce((acc, item) => {
                      const s = item.sector || 'unknown';
                      acc[s] = (acc[s] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([sector, count]) => (
                    <span key={sector} style={{ background: '#e0f2f1', color: '#1d6f6a', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                      {sector}: {count}
                    </span>
                  ))}
                </div>
              )}
              {batchSummary.total === 0 && (
                <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No batch found for {activeBatchDate}. Click "Run Today's Batch" to start.</span>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }} data-testid="batch-status-empty">
              No batch loaded. Click ↻ Refresh or Run Today's Batch.
            </div>
          )}

          {/* Legacy no-batch warning */}
          <LegacyBatchWarning count={legacyQueueCount} />

          {/* Source Health collapsible panel */}
          <div style={{ marginTop: 10 }}>
            <button
              onClick={() => { setSourceHealthOpen(o => !o); if (!sourceHealth) fetchSourceHealth(); }}
              data-testid="source-health-toggle"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#374151', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {sourceHealthOpen ? '▼' : '▶'} Source Health {sourceHealth ? `(${sourceHealth.enabled_sources} active / ${sourceHealth.total_sources} total)` : ''}
              {sourceHealth?.sectors_below_redundancy?.length === 6 && (
                <span style={{ background: '#fee2e2', color: '#991b1b', fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, marginLeft: 4 }}>ALL SECTORS BELOW 3×</span>
              )}
            </button>

            {sourceHealthOpen && (
              <div data-testid="source-health-panel" style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', fontSize: 11 }}>
                {sourceHealthLoading && <div style={{ padding: 12, color: '#9ca3af' }}>Loading source health…</div>}
                {!sourceHealthLoading && sourceHealth && Object.entries(sourceHealth.by_sector || {}).map(([sector, sources]) => (
                  <div key={sector} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ padding: '6px 10px', background: '#f9fafb', fontWeight: 700, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {sector}
                      {(sourceHealth.sector_summary?.[sector]?.enabled || 0) < 3 && (
                        <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 9, padding: '1px 4px', borderRadius: 3 }}>{sourceHealth.sector_summary[sector].enabled}/3 sources</span>
                      )}
                    </div>
                    {sources.map(src => (
                      <div key={src.id} data-testid={`source-row-${src.id}`} style={{ display: 'grid', gridTemplateColumns: '200px 80px 60px 70px 60px 60px 60px 1fr', gap: 6, padding: '5px 10px', alignItems: 'center', borderBottom: '1px solid #f9fafb', background: !src.enabled ? '#fafafa' : '#fff' }}>
                        <span style={{ fontWeight: 600, color: src.enabled ? '#111827' : '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={src.name}>{src.name}</span>
                        <HealthBadge status={src.health_status} />
                        <span style={{ color: src.enabled ? '#059669' : '#9ca3af', fontSize: 10 }}>{src.enabled ? 'active' : 'disabled'}</span>
                        <span style={{ color: '#6b7280', fontSize: 10 }} title="Last item inserted">{src.last_item_inserted_at ? new Date(src.last_item_inserted_at).toLocaleDateString() : '—'}</span>
                        <span style={{ color: '#6b7280', fontSize: 10 }} title="Last run fetched">f:{src.last_run_fetched ?? 0}</span>
                        <span style={{ color: src.last_run_inserted > 0 ? '#059669' : '#6b7280', fontSize: 10, fontWeight: src.last_run_inserted > 0 ? 700 : 400 }} title="Last run inserted">ins:{src.last_run_inserted ?? 0}</span>
                        <span style={{ color: '#6b7280', fontSize: 10 }} title="Last run duplicates">dup:{src.last_run_duplicates ?? 0}</span>
                        <span style={{ color: '#ef4444', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={src.warning || src.last_error || ''}>{src.warning || src.last_error || ''}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
          <select
            value={filters.sector}
            onChange={e => setFilters(p => ({ ...p, sector: e.target.value }))}
            style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 12px', fontSize: 12, background: '#fff', color: '#374151' }}
            data-testid="filter-sector"
          >
            {SECTORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <select
            value={filters.image_status}
            onChange={e => setFilters(p => ({ ...p, image_status: e.target.value }))}
            style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 12px', fontSize: 12, background: '#fff', color: '#374151' }}
            data-testid="filter-image-status"
          >
            {IMG_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <input
            value={filters.search}
            onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
            placeholder="Search title, source…"
            style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 12px', fontSize: 12, width: 200 }}
            data-testid="filter-search"
          />

          {(filters.sector || filters.image_status || filters.search) && (
            <button
              onClick={() => setFilters({ sector: '', image_status: '', search: '' })}
              style={{ fontSize: 12, color: '#6b7280', background: 'none', border: '1px solid #e5e7eb', borderRadius: 7, padding: '6px 10px', cursor: 'pointer' }}
              data-testid="clear-filters-btn"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#6b7280', fontSize: 13 }} data-testid="aer-loading">
            Loading drafts…
          </div>
        ) : drafts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#6b7280', fontSize: 13 }} data-testid="aer-empty">
            No engine drafts match the current filters.
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }} data-testid="drafts-table">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '10px 12px', width: 36 }}>
                    <input type="checkbox" checked={selected.size === drafts.length && drafts.length > 0} onChange={toggleAll}
                      data-testid="select-all-checkbox" />
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Title / Source</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Sector</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Image</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Queue</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Created</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map(item => (
                  <DraftRow
                    key={item.id}
                    item={item}
                    selected={selected.has(item.id)}
                    onToggle={toggleSelect}
                    onOpen={setOpenDraft}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        </>)}

      </div>

      {/* Review modal */}
      {openDraft && (
        <ReviewModal
          draft={openDraft}
          onClose={() => setOpenDraft(null)}
          onSaved={(updated) => {
            setDrafts(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
            setOpenDraft(updated);
          }}
          onPublished={(id) => {
            setDrafts(prev => prev.filter(d => d.id !== id));
            setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
            setOpenDraft(null);
          }}
          onRejected={(id) => {
            setDrafts(prev => prev.filter(d => d.id !== id));
            setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
            setOpenDraft(null);
          }}
        />
      )}
    </AdminLayout>
  );
}
